// Ported from vlookup-dramatic/components/ui/dialog.tsx without Base UI.
// Controlled: <Dialog open onOpenChange><DialogContent>…</DialogContent></Dialog>
import React, { createContext, useContext, useEffect } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/utils";
import { getOverlayRoot } from "../../lib/overlay-root";
import { XIcon } from "../../lib/icons";
import { Button } from "./button";

const Ctx = createContext({ open: false, setOpen: () => {} });

export function Dialog({ open = false, onOpenChange = () => {}, children }) {
  return <Ctx.Provider value={{ open, setOpen: onOpenChange }}>{children}</Ctx.Provider>;
}

export function DialogTrigger({ className, children, ...props }) {
  const { setOpen } = useContext(Ctx);
  return (
    <button type="button" data-slot="dialog-trigger" className={className} onClick={() => setOpen(true)} {...props}>
      {children}
    </button>
  );
}

export function DialogClose({ className, children, ...props }) {
  const { setOpen } = useContext(Ctx);
  return (
    <button type="button" data-slot="dialog-close" className={className} onClick={() => setOpen(false)} {...props}>
      {children}
    </button>
  );
}

export function DialogContent({ className, children, showCloseButton = true, ...props }) {
  const { open, setOpen } = useContext(Ctx);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, setOpen]);

  if (!open) return null;

  return createPortal(
    <>
      <div
        data-slot="dialog-overlay"
        className="fixed inset-0 isolate z-50 bg-black/10 backdrop-blur-xs"
        onClick={() => setOpen(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        data-slot="dialog-content"
        className={cn(
          "fixed top-1/2 left-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-xl bg-popover p-4 text-sm text-popover-foreground ring-1 ring-foreground/10 outline-none sm:max-w-sm",
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <Button variant="ghost" size="icon-sm" className="absolute top-3 right-3" onClick={() => setOpen(false)}>
            <XIcon />
            <span className="sr-only">Close</span>
          </Button>
        )}
      </div>
    </>,
    getOverlayRoot()
  );
}

export function DialogHeader({ className, ...props }) {
  return <div data-slot="dialog-header" className={cn("flex flex-col gap-1 text-center sm:text-left", className)} {...props} />;
}

export function DialogFooter({ className, ...props }) {
  return <div data-slot="dialog-footer" className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)} {...props} />;
}

export function DialogTitle({ className, children, ...props }) {
  return (
    <h2 data-slot="dialog-title" className={cn("text-base leading-none font-medium", className)} {...props}>
      {children}
    </h2>
  );
}

export function DialogDescription({ className, ...props }) {
  return <p data-slot="dialog-description" className={cn("text-sm text-muted-foreground", className)} {...props} />;
}
